import { Entity } from '@common/types/Entity';
import { EntityEvents } from '@common/user-analytics';
import { LSP } from '@LSP/base/types';
import { BackEndCodeLanguage, BackEndCodeLanguageType } from '@prophecy/graphql-types/enums';
import { ActorType, EventName, EventType, metrics } from '@prophecy/metrics';
import { message } from '@prophecy/ui-v3/ui-compat';
import { useImmerWithCallback, useOnMount, usePersistentCallback } from '@prophecy/utils/react/hooks';
import { validateEntityName } from '@prophecy/utils/validation';
import { useEffect, useMemo, useState } from 'react';

import { useMixPanel } from '../../../context/mixpanel/context';
import { DatasetEntityType, TranspilerEntityType } from '../../../data/apis/transpileTypes';
import { getTranspilerWSEndPoint } from '../../../utils/getServerUrl';
import { FileFormats } from '../config';
import { ImportStepValue, PipelineStatusType } from './steps/types';
import { getValidationSummary, hasValidationErrors } from './steps/utils';
import { Notifications, TranspilerClient } from './TranspilerClient';
import {
  AnalysisAction,
  DatasetAssociatedEntityType,
  defaultClientState,
  ResetState,
  TranspilationInitialStateType,
  TranspileClientStateType,
  TranspilerValidateEntityType,
  UploadActionType,
  UseTranspilationGraph
} from './types';
import { transpilerTypeString } from './utils';

const validateEntity = (
  { name, filePath, entity, index }: { name: string; filePath?: string; entity: string | Entity; index: number },
  nameList: string[]
) => {
  const error = validateEntityName(name || '');
  let errorMsg = '';
  const errorName = name ? name : `${entity} ${index + 1}`;
  if (error || !name) {
    errorMsg = `${error || `${entity} name required`} for ${errorName} `;
  } else if (!filePath) {
    errorMsg = `${error || `${entity} filePath required`} for ${errorName}`;
  } else if (nameList.includes(name)) {
    errorMsg = `Duplicate ${entity} name for ${errorName}`;
  }
  return errorMsg;
};

const getEntitiesFilteredData = (entities: TranspilerEntityType) => {
  const { jobs, pipelines, datasets = [] } = entities;
  return {
    pipelines: pipelines.map(({ __pathIndex, ...rest }) => rest),
    jobs: jobs.map(({ __pathIndex, ...rest }) => rest),
    datasets: datasets.map(({ __pathIndex, ...rest }) => rest)
  };
};

const validateEntities = ({ pipelines, jobs = [] }: TranspilerEntityType) => {
  let errorMsg = '';
  const nameList: string[] = [];
  pipelines.some((pipeline, i) => {
    errorMsg = validateEntity(
      { name: pipeline.name, filePath: pipeline.filePath, entity: 'Pipeline', index: i },
      nameList
    );
    if (pipeline?.configurations?.length && !errorMsg) {
      const configList: string[] = [];
      pipeline.configurations.some((config) => {
        errorMsg = validateEntity({ name: config.name, filePath: 'random', entity: 'config', index: i }, configList);
        configList.push(config.name);
        return !!errorMsg;
      });
    }
    nameList.push(pipeline.name);
    return !!errorMsg;
  });

  if (!errorMsg) {
    const jobNames: string[] = [];
    jobs.some((job, i) => {
      errorMsg = validateEntity({ name: job.name, filePath: job.filePath, entity: Entity.Job, index: i }, jobNames);
      jobNames.push(job.name);
      return !!errorMsg;
    });
  }

  if (errorMsg) {
    message.error({ content: errorMsg });
  }
  return errorMsg;
};

export function useTranspilationGraph({
  transpilerId,
  language,
  isReimport,
  handleStepChange,
  transpilerType,
  isUpdate,
  step,
  setUploadedPaths,
  transpilerEntities,
  resetTranspilerEntities,
  setPipelineDatasets,
  setDatasets
}: {
  language: BackEndCodeLanguageType;
  step: number;
  isReimport?: boolean;
  transpilerId?: string;
  transpilerType: string;
  isUpdate?: boolean;
  transpilerEntities: TranspilerEntityType;
  setUploadedPaths: (uploadedPaths: string[]) => void;
  handleStepChange: (step: number, isPrevious?: boolean) => void;
  resetTranspilerEntities: (source?: string) => void;
  setDatasets: (datasets: DatasetEntityType[], associatedEntities: { [key: string]: string[] }) => void;
  setPipelineDatasets: (transpilerEntities: TranspilerEntityType) => void;
}): UseTranspilationGraph {
  const [clientState, setClientState] = useImmerWithCallback<TranspileClientStateType>(defaultClientState);
  const [hasInitialStateLoaded, toggleInitialLoading] = useState(true);
  const { track } = useMixPanel();
  const lspClient = useMemo(
    () =>
      new TranspilerClient(`${getTranspilerWSEndPoint()}/${transpilerId}/connectToTranspilerWs`, { lazyInit: true }),
    [transpilerId]
  );

  const resetState = usePersistentCallback(() => {
    setClientState({
      ...defaultClientState,
      validate: {
        ...defaultClientState.validate,
        validatedData: clientState.validate.validatedData,
        hasSkippedValidation: clientState.validate.hasSkippedValidation
      },
      analysisSummary: {
        ...defaultClientState.analysisSummary,
        canDownload: clientState.analysisSummary.canDownload
      },
      savedEntities: {
        ...defaultClientState.savedEntities,
        associatedEntities: clientState.savedEntities.associatedEntities
      }
    });
  });

  const initializeConnection = usePersistentCallback(() => {
    if (transpilerId && !lspClient.connected) {
      lspClient.lazyInit();
    }
  });

  useEffect(() => {
    if (isUpdate) {
      setClientState((draft) => {
        draft.validate.hasSkippedValidation = false;
      });
    }
  }, [isUpdate, setClientState]);

  useEffect(() => {
    if (transpilerId) {
      initializeConnection();
    }
  }, [initializeConnection, transpilerId]);

  useOnMount(() => {
    if (step === ImportStepValue.TranspileGraph) {
      setClientState(
        (draft) => {
          draft.savedEntities.isTranspilation = true;
        },
        () => {
          initializeConnection();
        }
      );
    }
    // Note: We want to trigger this only when user coming from detail page
  });

  const cancelTranspilation = usePersistentCallback(() => {
    if (lspClient.connected) {
      setClientState((draft) => {
        draft.validate.isCancelling = true;
        draft.transpileSummary.hasCancelEnabled = false;
      });
      lspClient.stopTranspilation({ bulkTranspilerId: transpilerId as string });
    }
  });

  const resetTranspilationState = usePersistentCallback(async (isReset = false, source?: string) => {
    if (clientState.resetState.resetType !== ResetState.started) {
      setClientState((draft) => {
        draft.resetState.resetType = isReset ? ResetState.initial : ResetState.started;
        if (source) {
          draft.resetState.source = source;
        }
      });
    }
    if (isReset) {
      return;
    }
    if (lspClient.connected) {
      lspClient.resetState({
        bulkTranspilerId: transpilerId as string,
        newTranspilerType: source || (clientState.resetState.source as string)
      });
    } else {
      initializeConnection();
    }
  });

  const saveEntities = usePersistentCallback(() => {
    return lspClient.setEntities({
      bulkTranspilerId: transpilerId as string,
      entitiesRequest: {
        type: transpilerTypeString(transpilerType as FileFormats),
        ...getEntitiesFilteredData(transpilerEntities)
      }
    });
  });

  const startAnalysisAfterEntitySave = usePersistentCallback((state: TranspileClientStateType) => {
    lspClient.startAnalysis({
      bulkTranspilerId: transpilerId as string,
      language,
      enableSubgraph: true,
      generateConfigsOnly: state.savedEntities.isConfigGeneration
    });
  });

  const triggerActionOnConnectionOpen = usePersistentCallback(async (updatedState?: TranspileClientStateType) => {
    updatedState = updatedState || clientState;
    if (updatedState.validate.hasValidationStarted) {
      lspClient.startValidation({
        bulkTranspilerId: transpilerId as string,
        language,
        entitiesRequest: {
          type: transpilerTypeString(transpilerType as FileFormats),
          ...getEntitiesFilteredData(transpilerEntities)
        }
      });
    } else if (updatedState.savedEntities.isSaving) {
      saveEntities();
    } else if (updatedState.analysisSummary.analysisAction === AnalysisAction.transpiling) {
      startAnalysisAfterEntitySave(updatedState);
    } else if (updatedState.savedEntities.isTranspilation) {
      handleStepChange(ImportStepValue.TranspileGraph);
      resetState();
      lspClient.startOrConnectTranspiler({
        language,
        enableSubgraph: true,
        bulkTranspilerId: transpilerId as string,
        generateConfigsOnly: updatedState.savedEntities.isConfigGeneration
      });
    }

    //Trigger associated entities call even if validation started
    if (updatedState.savedEntities.associatedEntitiesLoading) {
      getAssociatedEntities();
    }

    //Load initial state for associated entities only if reimport
    if (hasInitialStateLoaded && isReimport && transpilerId) {
      lspClient.getInitialState({ bulkTranspilerId: transpilerId });
      toggleInitialLoading(false);
    }

    if (clientState.resetState.resetType === ResetState.started) {
      await resetTranspilationState();
    }
  });

  const startValidation = usePersistentCallback((shouldAnalyze?: boolean) => {
    if (validateEntities(transpilerEntities)) {
      return;
    }
    setClientState(
      (draft) => {
        draft.validate.hasValidationStarted = true;
        draft.savedEntities.isTranspilation = false;
        draft.validate.validatedData = undefined;
        draft.validate.validatingToAnalyze = shouldAnalyze;
      },
      (c) => {
        if (!lspClient.connected) {
          initializeConnection();
        } else {
          triggerActionOnConnectionOpen(c);
        }
      }
    );
  });

  const getAssociatedEntities = usePersistentCallback(() => {
    if (!clientState.savedEntities.associatedEntitiesLoading) {
      setClientState((draft) => {
        draft.savedEntities.associatedEntitiesLoading = true;
      });
    }

    if (!lspClient.connected) {
      initializeConnection();
    } else {
      lspClient.getAssociatedEntities({
        bulkTranspilerId: transpilerId as string,
        entitiesRequest: {
          type: transpilerTypeString(transpilerType as FileFormats),
          ...getEntitiesFilteredData(transpilerEntities)
        }
      });
    }
  });

  const setUploadPercentage = usePersistentCallback((percentage: number) => {
    if (clientState.upload.processingPercentage === 0 || clientState.upload.uploadAction === UploadActionType.upload) {
      setClientState(
        (draft) => {
          draft.upload.uploadPercentage = percentage;
          draft.upload.isUploading = true;
          draft.upload.processingPercentage = 0;
          draft.upload.uploadAction = percentage >= 100 ? UploadActionType.process : UploadActionType.upload;
        },
        () => {
          if (!lspClient.connected) {
            initializeConnection();
          }
        }
      );
    }
  });

  const resetUploadProgress = usePersistentCallback(() => {
    setClientState((draft) => {
      draft.upload.uploadPercentage = 0;
      draft.upload.processingPercentage = 0;
      draft.upload.isUploading = false;
      draft.upload.uploadAction = UploadActionType.upload;
    });
  });

  const startTranspilation = usePersistentCallback(
    async (hasValidation = false, isDialog?: boolean, callSaveEntities?: boolean) => {
      if (validateEntities(transpilerEntities)) {
        return;
      }
      if (!clientState.validate.hasSkippedValidation && (!hasValidation || (isUpdate && !isDialog))) {
        setClientState((draft) => {
          draft.validate.hasValidation = true;
        });
      } else {
        setClientState(
          (draft) => {
            draft.savedEntities.isTranspilation = !hasValidation;
            draft.validate.hasValidationStarted = hasValidation;
            // Skip: save via isSaving → setEntitiesSuccess → startOrConnect (don't await save).
            draft.savedEntities.isSaving = Boolean(isUpdate) || Boolean(callSaveEntities);
            draft.transpileSummary.statuses = [];
          },
          (c) => {
            if (!lspClient.connected) {
              initializeConnection();
            } else {
              triggerActionOnConnectionOpen(c);
            }
          }
        );
      }
    }
  );

  const handleValidateAction = usePersistentCallback((hasSave?: boolean) => {
    setClientState(
      (draft) => {
        draft.validate.hasValidation = false;
        draft.validate.hasSkippedValidation = hasSave === false;
        draft.savedEntities.hasSave = hasSave;
      },
      async () => {
        if (hasSave !== undefined) {
          const hasSkippedValidation = hasSave === false;
          await startTranspilation(hasSave, true, hasSkippedValidation);
        }
      }
    );
  });

  const stopValidation = usePersistentCallback(() => {
    lspClient.stopValidation({ bulkTranspilerId: transpilerId as string });
    setClientState((draft) => {
      draft.validate.isCancelling = false;
      draft.validate.hasValidationStarted = false;
      draft.savedEntities.hasSave = false;
      draft.validate.validatePercentage = 0;
    });
  });

  const resetAnalysis = usePersistentCallback(() => {
    setClientState((draft) => {
      draft.analysisSummary.analysisAction = AnalysisAction.initial;
      draft.savedEntities.isSaving = false;
      draft.analysisSummary.percentage = 0;
    });
  });

  const startAnalysis = usePersistentCallback(() => {
    if (clientState.analysisSummary.analysisAction !== AnalysisAction.transpiling) {
      setClientState((draft) => {
        draft.analysisSummary.analysisAction = AnalysisAction.transpiling;
        draft.savedEntities.isSaving = true;
        draft.analysisSummary.percentage = 0;
      });
    }

    if (!lspClient.connected) {
      // is isSaving is true, saveEntities is called on initialize
      initializeConnection();
    } else {
      // if analysisAction is transpiling, startAnalysis method is called on setEntitiesSucess callback
      saveEntities();
    }
  });

  const stopAnalysis = usePersistentCallback(() => {
    lspClient.stopAnalysisSession({ bulkTranspilerId: transpilerId as string });
    resetAnalysis();
  });

  const cancelAnalysis = usePersistentCallback(() => {
    if (
      clientState.analysisSummary.analysisAction === AnalysisAction.transpiling &&
      !clientState.savedEntities.isSaving
    ) {
      stopAnalysis();
    } else {
      resetAnalysis();
    }
  });

  const setAnalysisReportAvailable = usePersistentCallback(() => {
    setClientState((draft) => {
      draft.analysisSummary.canDownload = true;
    });
  });

  const toggleValidateSummary = usePersistentCallback(async (data: TranspilerValidateEntityType) => {
    if (clientState.validate.validatingToAnalyze) {
      setClientState((draft) => {
        draft.validate.validatingToAnalyze = false;
      });

      let shouldContinue = false;
      if (hasValidationErrors(data)) {
        shouldContinue = await getValidationSummary(data, true, language === BackEndCodeLanguage.sql);
      } else {
        shouldContinue = true;
      }

      if (shouldContinue) {
        startAnalysis();
        handleStepChange(ImportStepValue.AnalyzeGraph);
      }
      return;
    }

    const hasTranspile = clientState.savedEntities.hasSave === true;
    const shouldContinue = await getValidationSummary(data, hasTranspile, language === BackEndCodeLanguage.sql);
    if (!shouldContinue) {
      setClientState((draft) => {
        draft.savedEntities.isTranspilation = false;
        draft.validate.isCancelling = false;
        draft.validate.hasSkippedValidation = true;
        draft.validate.hasValidationStarted = false;
        draft.validate.validatePercentage = 0;
        draft.savedEntities.isSaving = false;
      });
      return;
    }
    if (hasTranspile) {
      await startTranspilation();
      handleStepChange(ImportStepValue.CompleteStep - 1, true);
    }
  });

  useEffect(() => {
    const fns: (() => void)[] = [];
    fns.push(
      lspClient.listen(LSP.Notification.open, () => {
        triggerActionOnConnectionOpen();
      })
    );

    fns.push(
      lspClient.listen(LSP.Notification.close, () => {
        setClientState((draft) => {
          draft.validate.hasValidationStarted = false;
          draft.validate.validatePercentage = 0;
          draft.validate.isCancelling = false;
          draft.savedEntities.isTranspilation = false;
        });
        lspClient.detach();
      })
    );
    fns.push(
      lspClient.listen(Notifications.bulkTranspilerProcessInitialUpdate, (resp) => {
        setClientState((draft) => {
          draft.transpileSummary.statuses = resp.params.statuses as PipelineStatusType[];
          draft.transpileSummary.hasCancelEnabled = true;
        });
      })
    );

    fns.push(
      lspClient.listen(Notifications.getAssociatedEntitiesError, () => {
        setClientState((draft) => {
          draft.savedEntities.associatedEntitiesLoading = false;
          draft.savedEntities.associatedEntitiesLoaded = false;
        });
      })
    );

    fns.push(
      lspClient.listen(Notifications.getAssociatedEntitiesSuccess, (resp) => {
        setClientState((draft) => {
          const associatedEntities = resp.params.associatedEntities as TranspilationInitialStateType;
          draft.savedEntities.associatedEntities = associatedEntities;
          draft.savedEntities.associatedEntitiesLoading = false;
          draft.savedEntities.associatedEntitiesLoaded = true;
          const datasetEntities = associatedEntities?.datasets as unknown as DatasetAssociatedEntityType;
          const datasets = datasetEntities?.datasetInfo;
          if (datasets) {
            setDatasets(datasets, datasetEntities.associatedPipelines);
          }
        });
      })
    );

    fns.push(
      lspClient.listen(Notifications.error, () => {
        resetState();
      })
    );

    fns.push(
      lspClient.listen(Notifications.resetSuccess, () => {
        setClientState((draft) => {
          draft.resetState.resetType = ResetState.succeeded;
          resetTranspilerEntities(draft.resetState.source);
        });
      })
    );

    fns.push(
      lspClient.listen(Notifications.bulkTranspileEstimation, (resp) => {
        setClientState((draft) => {
          draft.transpileSummary.estimationTime = resp.params.estimationTime;
        });
      })
    );

    fns.push(
      lspClient.listen(Notifications.bulkTranspilerProcessUpdate, (resp) => {
        const currentStatus = resp.params.statuses?.[0] as PipelineStatusType;
        const child = currentStatus?.children?.[0];
        setClientState((draft) => {
          const statusIndex = draft.transpileSummary.statuses.findIndex(
            (statusDetail) => statusDetail.id === currentStatus.id
          );
          const currentStatusDetail = draft.transpileSummary.statuses[statusIndex];
          if (currentStatusDetail) {
            const currentChildIndex = currentStatusDetail?.children?.findIndex((child1) => child1.id === child?.id);
            if (currentChildIndex !== undefined && child && currentChildIndex > -1 && currentStatusDetail.children) {
              currentStatusDetail.children[currentChildIndex] = child;
            }
            currentStatusDetail.transpilationStatus = currentStatus.transpilationStatus;
            currentStatusDetail.message = currentStatus.message;
            currentStatusDetail.percent = currentStatus.percent;
            draft.transpileSummary.statuses[statusIndex] = currentStatusDetail;
          }
        });
      })
    );

    fns.push(
      lspClient.listen(Notifications.validationUpdate, (resp) => {
        setClientState((draft) => {
          draft.validate.validatePercentage = resp.params.percent as number;
        });
      })
    );

    fns.push(
      lspClient.listen(Notifications.validationError, (resp) => {
        setClientState((draft) => {
          draft.transpileSummary.isError = true;
          draft.validate.hasValidationStarted = false;
          draft.validate.validatePercentage = 0;
        });
        message.error({ content: resp.params?.message || 'Validation failed.' });
      })
    );

    fns.push(
      lspClient.listen(Notifications.validationSuccess, (resp) => {
        const data = resp.params as TranspilerValidateEntityType;
        setClientState(
          (draft) => {
            draft.validate.hasValidationStarted = false;
            draft.validate.hasSkippedValidation = true;
            draft.validate.validatedData = data;
          },
          async () => {
            await toggleValidateSummary(data);
          }
        );
      })
    );

    fns.push(
      lspClient.listen(Notifications.setEntitiesSuccess, async (resp) => {
        setClientState(
          (draft) => {
            if (draft.analysisSummary.analysisAction === AnalysisAction.initial) {
              handleStepChange(ImportStepValue.CompleteStep - 1);
              draft.savedEntities.isTranspilation = true;
              draft.savedEntities.isConfigGeneration = !!resp.params?.generateConfig;
            }
            draft.savedEntities.isSaving = false;
            draft.savedEntities.hasSave = false;
          },
          (c) => {
            triggerActionOnConnectionOpen(c);
          }
        );
      })
    );

    fns.push(
      lspClient.listen(Notifications.setEntitiesError, (resp) => {
        setClientState((draft) => {
          draft.transpileSummary.isError = true;
          draft.savedEntities.hasSave = false;
          draft.savedEntities.isSaving = false;
          draft.analysisSummary.analysisAction = AnalysisAction.initial;
        });
        message.error({ content: resp.params?.message || 'Unable to save the entities for transpilation.' });
      })
    );

    fns.push(
      lspClient.listen(Notifications.stopBulkTranspilerSessionSuccess, () => {
        resetState();
        handleStepChange(ImportStepValue.TranspileGraph - 1);
      })
    );

    fns.push(
      lspClient.listen(Notifications.uploadProcessingUpdate, (resp) => {
        const currentPercentage = (resp.params?.percent as number) || 0;
        setClientState((draft) => {
          draft.upload.processingPercentage = currentPercentage;
          draft.upload.uploadPercentage = 100;
          draft.upload.uploadAction = UploadActionType.process;
        });
      })
    );

    fns.push(
      lspClient.listen(Notifications.uploadProcessingFailed, (resp) => {
        const content = resp.params?.msg || 'Upload failed';
        const paths = resp.params.paths as string[];
        message.error({ content });
        resetUploadProgress();
        if (paths.length > 0) {
          setUploadedPaths(paths);
        }
      })
    );

    fns.push(
      lspClient.listen(Notifications.uploadProcessingSuccess, (resp) => {
        const paths = resp.params.paths as string[];
        resetUploadProgress();
        message.success({ content: 'File uploaded successfully.', duration: 700 });
        if (paths.length > 0) {
          setUploadedPaths(paths);
        }
      })
    );

    fns.push(
      lspClient.listen(Notifications.stopBulkTranspilerValidationSessionSuccess, () => {
        resetState();
      })
    );
    fns.push(
      lspClient.listen(Notifications.bulkTranspilerProcessSuccess, (resp) => {
        setClientState((draft) => {
          draft.transpileSummary.statuses = resp.params.statuses as PipelineStatusType[];
          draft.transpileSummary.isSuccess = true;
          draft.transpileSummary.isError = false;
          draft.savedEntities.isSaving = false;
          draft.savedEntities.isTranspilation = false;
          draft.savedEntities.hasSave = false;
          draft.validate.hasValidationStarted = false;
        });
        track(EntityEvents.transpile.home.transpilationSuccess, { transpilerId });
        metrics.track({
          eventName: EventName.ui.transpiler.import.transpilation.success,
          eventType: EventType.userAction,
          actorType: ActorType.system,
          payload: { transpilerId: transpilerId ?? '' }
        });
      })
    );

    fns.push(
      lspClient.listen(Notifications.analysisProcessUpdate, ({ params }) => {
        setClientState((draft) => {
          if (params?.estimationTime) {
            draft.analysisSummary.estimationTime = params.estimationTime;
          }
          draft.analysisSummary.percentage = params.progressPercent;
        });
      })
    );
    fns.push(
      lspClient.listen(Notifications.analysisFailure, ({ params }) => {
        message.success({ content: params?.message || 'Analysis failed.', duration: 700 });
        setClientState((draft) => {
          draft.analysisSummary.analysisAction = AnalysisAction.error;
          draft.analysisSummary.percentage = 100;
          draft.analysisSummary.canDownload = false;
        });
      })
    );
    fns.push(
      lspClient.listen(Notifications.analysisSuccess, ({ params }) => {
        setClientState((draft) => {
          draft.analysisSummary.summary = params;
          draft.analysisSummary.analysisAction = AnalysisAction.success;
          draft.analysisSummary.percentage = 100;
          draft.analysisSummary.canDownload = true;
        });
      })
    );

    fns.push(
      lspClient.listen(Notifications.bulkTranspilerProcessError, (resp) => {
        setClientState((draft) => {
          draft.transpileSummary.statuses = resp.params.statuses as PipelineStatusType[];
          draft.transpileSummary.isError = true;
          draft.validate.hasValidationStarted = false;
        });
        track(EntityEvents.transpile.home.transpilationFailed, { transpilerId });
        metrics.track({
          eventName: EventName.ui.transpiler.import.transpilation.failed,
          eventType: EventType.userAction,
          actorType: ActorType.system,
          payload: { transpilerId: transpilerId ?? '' }
        });
      })
    );

    fns.push(
      lspClient.listen(LSP.Notification.error, ({ params }) => {
        message.error({ content: params?.msg || 'Some error occurred.' });
        setClientState((draft) => {
          draft.transpileSummary.isError = true;
          draft.validate.hasValidationStarted = false;
          draft.savedEntities.isSaving = false;
          draft.validate.validatePercentage = 0;
          draft.validate.isCancelling = false;
          draft.analysisSummary.analysisAction = AnalysisAction.initial;
        });
      })
    );

    return () => {
      fns.forEach((fn) => fn());
      lspClient.detach();
    };
  }, [
    lspClient,
    startTranspilation,
    setPipelineDatasets,
    setUploadedPaths,
    resetTranspilerEntities,
    resetState,
    startAnalysisAfterEntitySave,
    triggerActionOnConnectionOpen,
    track,
    setClientState,
    transpilerId,
    handleStepChange,
    setDatasets,
    toggleValidateSummary,
    resetUploadProgress
  ]);

  return useMemo(
    () =>
      ({
        clientState,
        overallPercentage: (clientState.upload.processingPercentage + clientState.upload.uploadPercentage) / 2,
        actions: {
          resetTranspilationState,
          cancelTranspilation,
          handleValidateAction,
          startAnalysis,
          stopAnalysis,
          cancelAnalysis,
          resetAnalysis,
          setAnalysisReportAvailable,
          startTranspilation,
          startValidation,
          setUploadPercentage,
          stopValidation,
          resetState,
          getAssociatedEntities,
          resetUploadProgress,
          initializeConnection
        }
      }) satisfies UseTranspilationGraph,
    [
      clientState,
      startAnalysis,
      stopAnalysis,
      cancelAnalysis,
      resetAnalysis,
      setAnalysisReportAvailable,
      resetTranspilationState,
      getAssociatedEntities,
      setUploadPercentage,
      startValidation,
      startTranspilation,
      handleValidateAction,
      stopValidation,
      resetState,
      cancelTranspilation,
      resetUploadProgress,
      initializeConnection
    ]
  );
}
